import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

import {
  clearCsrfTokenCache,
  clearMemoryAuthTokens,
  refreshSession,
  request,
  setMemoryAuthTokens,
  unwrapZephixClientPayload,
} from "@/lib/api";
import { clearApiClientCsrfCache } from "@/lib/api/client";
import { endClientSession } from "@/auth/endClientSession";
import { setAuthOrganizationId, setAuthPlatformRole } from "@/state/authContextBridge";
import { clearWorkspaceScopedClientState } from "@/state/workspaceClientState";
import { coldBootRestoreSession } from "@/state/coldBootSession";
import type { WorkspaceMembershipSummary } from "@/lib/auth/auth.types";
import { submitMfaChallenge } from "@/lib/auth/auth.api";

const LAST_ORG_KEY = "zephix.lastOrgId";

export type LoginOutcome =
  | { ok: true; user: AuthUser }
  | { ok: false; mfaRequired: true; challengeToken: string };

function applyOrgChangeReset(currentOrgId: string | null | undefined) {
  // Missing org context: no-op and do not write lastOrgId.
  if (!currentOrgId) return;

  try {
    const lastOrgId = localStorage.getItem(LAST_ORG_KEY);

    // First hydrated org write: set only, never clear.
    if (!lastOrgId) {
      localStorage.setItem(LAST_ORG_KEY, currentOrgId);
      return;
    }

    // Same org: no-op.
    if (lastOrgId === currentOrgId) {
      return;
    }

    // Org changed: clear workspace-scoped state and persist new org marker.
    if (lastOrgId !== currentOrgId) {
      clearWorkspaceScopedClientState();
      localStorage.setItem(LAST_ORG_KEY, currentOrgId);
    }
  } catch {
    // Best effort only.
  }
}

type PlatformRole = "ADMIN" | "MEMBER" | "VIEWER" | "GUEST";

type AuthUser = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  profilePicture?: string | null;
  platformRole?: PlatformRole;
  /** @deprecated Use platformRole instead */
  role?: string;
  organizationId?: string;
  /** From /auth/me: either legacy string[] or object with isAdmin (org admin / owner). */
  permissions?: string[] | { isAdmin?: boolean; [k: string]: unknown } | null;
  /** Subset of org from /auth/me (id, name, slug). */
  organization?: { id: string; name: string; slug: string } | null;
  /** Present when backend publishes MFA enrollment state on `/auth/me`. */
  mfaEnrolled?: boolean;
  /** ISO — org-admin MFA enforcement grace window (optional). */
  mfaGracePeriodEndsAt?: string | null;
  /** Stream A optional workspace membership projection on `/auth/me`. */
  workspaceMemberships?: WorkspaceMembershipSummary[];
};

type AuthContextValue = {
  user: AuthUser | null;
  isLoading: boolean;
  /** @deprecated Use isLoading instead */
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<LoginOutcome>;
  completeMfaLogin: (body: { challengeToken: string; code: string }) => Promise<AuthUser>;
  /** After invitation accept (201) or similar — installs tokens and reloads `/auth/me`. */
  bootstrapSessionFromTokens: (accessToken: string, refreshToken: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<AuthUser | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

let inFlightMe: Promise<AuthUser | null> | null = null;

/** Drop the single-flight slot so the next `/auth/me` is a fresh request (e.g. after memory JWT is set). */
function resetFetchMeDedupe(): void {
  inFlightMe = null;
}

function pickLoginTokens(payload: unknown): { access: string | null; refresh: string | null } {
  const flat = unwrapZephixClientPayload<Record<string, unknown>>(payload);
  if (!flat || typeof flat !== "object") {
    return { access: null, refresh: null };
  }
  let access = typeof flat.accessToken === "string" ? flat.accessToken : null;
  let refresh = typeof flat.refreshToken === "string" ? flat.refreshToken : null;
  const nested = flat.data;
  if ((!access || !refresh) && nested && typeof nested === "object") {
    const d = nested as Record<string, unknown>;
    if (!access && typeof d.accessToken === "string") access = d.accessToken;
    if (!refresh && typeof d.refreshToken === "string") refresh = d.refreshToken;
  }
  return { access, refresh };
}

async function fetchMeSingleFlight(): Promise<AuthUser | null> {
  if (inFlightMe) return inFlightMe;

  inFlightMe = (async () => {
    try {
      const res = await request.get<Record<string, unknown>>("/auth/me");
      const unwrapped = unwrapZephixClientPayload(res) ?? res;

      // Accept both shapes:
      // 1) { user: {...} } or { user: null }
      // 2) direct user object {...}
      // Also tolerate legacy wrappers that may still include `data`.
      const payload = (unwrapped ?? {}) as Record<string, unknown>;
      const source =
        ((payload.data as Record<string, unknown> | undefined)?.user as
          | Record<string, unknown>
          | null
          | undefined) ??
        ((payload.data as Record<string, unknown> | undefined) as
          | Record<string, unknown>
          | null
          | undefined) ??
        ((payload.user as Record<string, unknown> | null | undefined) as
          | Record<string, unknown>
          | null
          | undefined) ??
        payload;

      if (!source || typeof source !== "object") {
        return null;
      }

      const id =
        (source.id as string | undefined) ||
        (source.userId as string | undefined) ||
        "";
      if (!id) {
        return null;
      }

      const perms = source.permissions as
        | string[]
        | { isAdmin?: boolean; [k: string]: unknown }
        | null
        | undefined;
      const orgFromMe = source.organization as
        | { id?: string; name?: string; slug?: string }
        | null
        | undefined;

      const wsRaw = (source.workspaceMemberships ?? source.workspaces) as unknown;
      let workspaceMemberships: WorkspaceMembershipSummary[] | undefined;
      if (Array.isArray(wsRaw)) {
        workspaceMemberships = wsRaw
          .map((row: unknown) => {
            if (!row || typeof row !== "object") return null;
            const r = row as Record<string, unknown>;
            const workspaceId = String(r.workspaceId ?? r.workspace_id ?? "");
            const workspaceName = String(r.workspaceName ?? r.workspace_name ?? "Workspace");
            const accessLevel = String(r.accessLevel ?? r.access_level ?? r.role ?? "");
            if (!workspaceId) return null;
            return { workspaceId, workspaceName, accessLevel };
          })
          .filter(Boolean) as WorkspaceMembershipSummary[];
      }

      const normalized: AuthUser = {
        id,
        email: (source.email as string | undefined) || "",
        firstName: (source.firstName as string | null | undefined) ?? null,
        lastName: (source.lastName as string | null | undefined) ?? null,
        profilePicture: (source.profilePicture as string | null | undefined) ?? null,
        platformRole: (source.platformRole as PlatformRole | undefined) || undefined,
        role: (source.role as string | undefined) || undefined,
        organizationId:
          (source.organizationId as string | undefined) ||
          (orgFromMe?.id ?? undefined),
        permissions: perms ?? undefined,
        organization:
          orgFromMe?.id && orgFromMe.name && orgFromMe.slug
            ? {
                id: orgFromMe.id,
                name: orgFromMe.name,
                slug: orgFromMe.slug,
              }
            : null,
        mfaEnrolled:
          typeof source.mfaEnrolled === "boolean"
            ? source.mfaEnrolled
            : typeof source.mfa_enabled === "boolean"
              ? source.mfa_enabled
              : undefined,
        mfaGracePeriodEndsAt:
          (source.mfaGracePeriodEndsAt as string | undefined) ??
          (source.mfa_grace_period_ends_at as string | undefined) ??
          null,
        workspaceMemberships,
      };

      return normalized;
    } catch {
      return null;
    } finally {
      inFlightMe = null;
    }
  })();

  return inFlightMe;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hydratedRef = useRef(false);
  /** Bumped on login/logout so a slow initial `/auth/me` cannot overwrite state after credentials are applied. */
  const authEpochRef = useRef(0);

  useEffect(() => {
    setAuthOrganizationId(user?.organizationId ?? null);
    setAuthPlatformRole(user?.platformRole ?? user?.role ?? null);
  }, [user]);

  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    (async () => {
      setIsLoading(true);
      const epoch = authEpochRef.current;
      // FIX-LOGOUT-ON-REFRESH-1: memory JWTs are gone on full reload.
      // If /auth/me is null/401→null, refreshSession once (cookie), then /auth/me again.
      const me = await coldBootRestoreSession({
        fetchMe: () => {
          resetFetchMeDedupe();
          return fetchMeSingleFlight();
        },
        refreshSession,
      });
      if (authEpochRef.current !== epoch) {
        // Login/logout advanced epoch — do not touch isLoading (caller owns it) or user.
        return;
      }
      applyOrgChangeReset(me?.organizationId);
      setUser(me);
      setIsLoading(false);
    })();
  }, []);

  async function refreshMe() {
    // AUTH-ME-401-NO-REFRESH-1 (#1571): an expired access token makes /auth/me
    // 401 → null (the interceptor does not refresh /auth/me). Before treating
    // that as signed out, renew once (one renewal across tabs, #1558) and
    // re-check once — the cold-boot rule. Never more than one of each.
    let fetches = 0;
    const me = await coldBootRestoreSession({
      fetchMe: () => {
        if (fetches++ > 0) resetFetchMeDedupe();
        return fetchMeSingleFlight();
      },
      refreshSession,
    });
    applyOrgChangeReset(me?.organizationId);
    setUser(me);
    return me;
  }

  async function login(email: string, password: string): Promise<LoginOutcome> {
    setIsLoading(true);
    try {
      authEpochRef.current += 1;
      resetFetchMeDedupe();
      clearCsrfTokenCache();
      clearApiClientCsrfCache();
      clearMemoryAuthTokens();
      const loginPayload = await request.post<Record<string, unknown>>("/v1/auth/login", { email, password });
      const flat =
        unwrapZephixClientPayload<Record<string, unknown>>(loginPayload) ??
        (loginPayload as Record<string, unknown> | null);
      if (flat && typeof flat === "object") {
        const mc = flat.mfaChallenge as Record<string, unknown> | undefined;
        if (mc && typeof mc.challengeToken === "string") {
          return { ok: false, mfaRequired: true, challengeToken: mc.challengeToken };
        }
        const code = flat.code as string | undefined;
        if (code === "MFA_REQUIRED" && typeof flat.challengeToken === "string") {
          return { ok: false, mfaRequired: true, challengeToken: flat.challengeToken };
        }
      }
      const { access, refresh } = pickLoginTokens(loginPayload);
      setMemoryAuthTokens(access, refresh);
      // Critical: initial app `/auth/me` may still be in-flight without Bearer — do not await that promise.
      resetFetchMeDedupe();
      const me = await fetchMeSingleFlight();
      applyOrgChangeReset(me?.organizationId);
      setUser(me);
      if (!me) {
        clearMemoryAuthTokens();
        throw new Error("Login succeeded but session not established");
      }
      return { ok: true, user: me };
    } finally {
      setIsLoading(false);
    }
  }

  async function completeMfaLogin(body: { challengeToken: string; code: string }) {
    setIsLoading(true);
    try {
      authEpochRef.current += 1;
      resetFetchMeDedupe();
      clearCsrfTokenCache();
      clearApiClientCsrfCache();
      clearMemoryAuthTokens();
      const loginPayload = await submitMfaChallenge({
        challengeToken: body.challengeToken,
        code: body.code,
      });
      const { access, refresh } = pickLoginTokens(loginPayload);
      setMemoryAuthTokens(access, refresh);
      resetFetchMeDedupe();
      const me = await fetchMeSingleFlight();
      applyOrgChangeReset(me?.organizationId);
      setUser(me);
      if (!me) {
        clearMemoryAuthTokens();
        throw new Error("MFA verification succeeded but session not established");
      }
      return me;
    } finally {
      setIsLoading(false);
    }
  }

  async function bootstrapSessionFromTokens(accessToken: string, refreshToken: string) {
    setIsLoading(true);
    try {
      authEpochRef.current += 1;
      resetFetchMeDedupe();
      clearCsrfTokenCache();
      clearApiClientCsrfCache();
      clearMemoryAuthTokens();
      setMemoryAuthTokens(accessToken || null, refreshToken || null);
      resetFetchMeDedupe();
      const me = await fetchMeSingleFlight();
      applyOrgChangeReset(me?.organizationId);
      setUser(me);
      if (!me) {
        clearMemoryAuthTokens();
        throw new Error("Session could not be established");
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function logout() {
    setIsLoading(true);
    try {
      authEpochRef.current += 1;
      resetFetchMeDedupe();
      setUser(null);
      await endClientSession();
    } finally {
      setIsLoading(false);
    }
  }

  const value = useMemo<AuthContextValue>(() => {
    return {
      user,
      isLoading,
      loading: isLoading,
      isAuthenticated: Boolean(user),
      login,
      completeMfaLogin,
      bootstrapSessionFromTokens,
      logout,
      refreshMe,
    };
  }, [user, isLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
